import BaseWidget, {WidgetProps} from '../../../js/WidgetBaseClass';

class InstitutionStatisticsProps extends WidgetProps {
    selectors = {
        sections: '.institution-statistics__report',
        sectionRadio: '.institution-statistics__report__input',
        subjectsController: '.institution-statistics__assessments__type',
        subjects: '.institution-statistics__assessments__subjects',
        subjectRadio: '.institution-statistics__assessments__subject',
        selectToggle: '.institution-statistics__assessments__subject__select__toggle',
        optionInput: '.institution-statistics__option__input',
        mail: '.institution-statistics__mail',
        loader: '.institution-statistics__loading__wrapper',
        responseMessage: '.institution-statistics__msg',
        iconableInputs: '.institution-statistics__iconable__input__wrapper',
    };
    elements = {
        sections: [],
        sectionRadio: [],
        subjectsController: [],
        subjects: [],
        subjectRadio: [],
        optionInput: [],
        mail: [],
        loader: [],
        responseMessage: [],
        iconableInputs: [],
    };
}

export default class InstitutionStatistics extends BaseWidget {
    init() {
        this.setElements();
        this.addListeners();
        this.handleLoadFocus();
    }

    setElements() {
        const container = this.props.container;

        for (let key in this.props.elements) {
            this.props.elements[key] = this.getElementsArray(this.props.selectors[key], container);
        }
        this.props.elements.loader = this.props.elements.loader.pop();
        this.props.elements.responseMessage = this.props.elements.responseMessage.pop();
        this.props.elements.mail = this.props.elements.mail.pop();
    }

    addListeners() {
        super.addListeners();

        this.props.container.addEventListener('submit', this.onSubmit.bind(this));
        this.props.elements.sectionRadio.forEach(item => {
            item.addEventListener('input', this.onSectionChange.bind(this));
        });
        this.props.elements.subjectRadio.forEach(item => {
            item.addEventListener('input', this.onSubjectRadioChange.bind(this));
        });
        this.props.elements.subjectsController.forEach(item => {
            item.addEventListener('input', this.onSubjectController.bind(this));
        });
        this.props.elements.optionInput.forEach(item => {
            item.addEventListener('change', this.onReportOptionChange.bind(this));
        });
        this.props.elements.iconableInputs.forEach(item => {
            item.addEventListener('click', this.onIconableIconWrapperClick.bind(this));
        });
    }

    handleLoadFocus() {
        const sections = this.props.elements.sections;

        sections.forEach((section, i) => {
            if (section.classList.contains('always-active')) return;
            const sectionRadioSelector = this.props.selectors.sectionRadio.replace('.', '');

            if (i === 0) {
                let radio = section.querySelector(this.props.selectors.sectionRadio);

                radio.checked = true;
                if (sections.length <= 2) radio.closest('label')?.classList.add('hidden'); // hide radio if there is only one optional section, this assumes that the "Report Options" section is always rendered
                this.setInputsState(section, true, sectionRadioSelector);
            } else {
                this.setInputsState(section, false, sectionRadioSelector);
            }
        });
    }

    get uri() {
        return this.props.container.getAttribute('action');
    }

    getPropsInstance(container) {
        return new InstitutionStatisticsProps(container);
    }

    setSelectToggleState(section, active) {
        const selectToggle = this.getElementsArray(this.props.selectors.selectToggle, section);

        selectToggle.forEach(toggle => {
            if (active) {
                const subSubjectsRadio = toggle.closest(this.props.selectors.subjects)?.querySelector('.empty');

                if (subSubjectsRadio && subSubjectsRadio.checked) {
                    toggle.classList.remove('disabled');
                } else {
                    toggle.classList.add('disabled');
                }
            } else {
                toggle.classList.add('disabled');
            }
        });
    }

    setInputsState(section, active, sectionRadioSelector) {
        const inputFields = this.getElementsArray('input', section);

        inputFields.forEach(field => {
            if (field.classList.contains(sectionRadioSelector)) return;
            const parent = field.parentElement; // for dropdowns

            if (active) {
                field.removeAttribute('disabled');
                parent?.classList.remove('disabled');
            } else {
                if (parent?.classList.contains('checkbox--primary')) field.checked = false; // uncheck checkboxes

                field.setAttribute('disabled', true);
                parent?.classList.add('disabled');
            }
        });
    }

    onSectionChange(e) {
        const {currentTarget} = e,
            currentSection = currentTarget.closest(this.props.selectors.sections),
            sectionRadioSelector = this.props.selectors.sectionRadio.replace('.', '');

        this.props.elements.sections.forEach(item => {
            if (item.classList.contains('always-active')) return;

            const active = item === currentSection;

            this.setSelectToggleState(item, active);
            this.setInputsState(item, active, sectionRadioSelector);
        });
    }

    onSubjectRadioChange(e) {
        const {currentTarget} = e;
        const toggle = currentTarget
            .closest(this.props.selectors.subjects)
            ?.querySelector(this.props.selectors.selectToggle);

        if (currentTarget.classList.contains('empty') && currentTarget.checked) {
            toggle.classList.remove('disabled');
        } else {
            toggle.classList.add('disabled');
        }
    }

    onSubjectController(e) {
        const {currentTarget} = e,
            id = currentTarget.id;

        this.props.elements.subjects.forEach(subject => {
            const isCurrent = subject.getAttribute('aria-labelledBy') === id;

            if (isCurrent) {
                subject.classList.remove('hidden');
                const subjectRadio = subject.querySelector(`${this.props.selectors.subjectRadio}:not(.empty)`);

                if (subjectRadio) subjectRadio.checked = true;
            } else {
                subject.classList.add('hidden');
                this.getElementsArray('input', subject).forEach(item => (item.checked = false));
            }
        });
    }

    onReportOptionChange(e) {
        // wrapped in try...catch just to be safe and to not trigger any issue in the console.
        try {
            const mail = this.props.elements.mail.childNodes[1]; // always input

            if (e.currentTarget.value === 'email') {
                this.props.elements.mail.classList.remove('disabled');
                mail.removeAttribute('disabled');
                mail.setAttribute('required', true);
            } else {
                this.props.elements.mail.classList.add('disabled');
                mail.setAttribute('disabled', true);
                mail.removeAttribute('required');
            }
        } catch (error) {
            console.log(error);
        }
    }

    onIconableIconWrapperClick(e) {
        const {currentTarget} = e;
        const input = currentTarget.querySelector('input');

        if (input) input.focus();
    }

    handleMessage(action, firstMessage, secondMessage) {
        let messageEle = this.props.elements.responseMessage;
        if (!messageEle) return;

        // always resets at the start
        messageEle.innerHTML = null;

        messageEle.textContent = firstMessage;
        messageEle.classList.remove('primary-text');
        messageEle.classList.remove('error');
        if (secondMessage) {
            messageEle.appendChild(document.createElement('br'));
            messageEle.appendChild(document.createTextNode(secondMessage));
        }

        switch (action) {
            case 'success': {
                messageEle.classList.add('primary-text');
                break;
            }
            case 'warning': {
                messageEle.classList.add('warning');
                break;
            }
            case 'error': {
                messageEle.classList.add('error');
                break;
            }
            case 'reset': {
                messageEle.innerHTML = null;
                break;
            }
        }
    }

    handleLoading(action) {
        const loader = this.props.elements.loader;
        if (!loader) return;

        // always hide at the start
        loader.classList.add('hidden');
        switch (action) {
            case 'show': {
                loader.classList.remove('hidden');
                break;
            }
            case 'hide': {
                // this exists just as a reminder that you could hide the loading element
                break;
            }
        }
    }

    async onSubmit(e) {
        e.preventDefault();
        try {
            this.handleLoading('show');
            this.handleMessage('reset');

            let form = e.currentTarget,
                {inputs, isValid, isDownload} = this.filterInputs(form);

            if (!form) return;
            if (!isValid) {
                this.handleMessage('warning', 'Invalid form, please check the fields and try again.');
                return;
            }

            let queryText = this.getQueryString(inputs),
                response = await this.handleRequest(form, queryText);

            if (!response) {
                this.handleMessage('error', 'There was an error while trying to submit the form.');
                return;
            }

            await this.handleResponse(response, isDownload);
        } catch (error) {
            this.handleMessage('error', 'Something went wrong');
            console.log(error);
        } finally {
            this.handleLoading('hide');
        }
    }

    getURLSearchParams(queryText) {
        if (!queryText) return '';

        return queryText.split('&').reduce(function (qs, query) {
            let chunks = query.split('='),
                key = chunks[0],
                value = chunks[1] || '',
                valueLower = value.trim().toLowerCase();
            if (valueLower === 'true') {
                value = true;
            } else if (valueLower === 'false') {
                value = false;
            } else if (Number(value)) {
                value = Number(value);
            }
            qs[key] = encodeURIComponent(value);
            return qs;
        }, {});
    }

    filterInputs(form) {
        let finalObj = {inputs: {}, isValid: true, isDownload: false};
        finalObj.inputs = this.getElementsArray('input:not([disabled]), select', form).filter(input => {
            const errorElement = input.name === 'institutionId' ? input.nextSibling : null;
            errorElement?.classList.add('hidden');
            if (input.name === 'renderOption' && input.value === 'download' && input.checked)
                finalObj.isDownload = true;

            if (input.disabled) return false;
            if (['radio', 'checkbox'].indexOf(input.type) > -1) return input.checked && !!input.value;

            if (!input.value || input.value === '-1') {
                if (errorElement) {
                    errorElement.classList.remove('hidden');
                    input.focus();
                    finalObj.isValid = false;
                }
                return false;
            }
            return true;
        });
        return finalObj;
    }

    async getJson(response) {
        let finalObj = {json: {}, isJson: true};
        try {
            finalObj.json = await response.json();
        } catch {
            finalObj.isJson = false;
        }
        return finalObj;
    }

    async handleRequest(form, queryText) {
        try {
            let response;
            if (form.dataset.useBody) {
                response = await fetch(this.uri, {method: 'POST', body: queryText});
            } else {
                // defaults to using a query string
                response = await fetch(`${this.uri}?${queryText}`, {method: 'POST'});
            }
            return response;
        } catch (e) {
            console.log(e);
        }
    }

    async handleResponse(response, isDownload) {
        if (isDownload) {
            // if download option was selected
            await this.handleDownloadResponse(response);
        } else {
            // if email option was selected (meaning none of the above if()'s were true)
            await this.handleEmailResponse(response);
        }
    }

    getQueryString(inputs) {
        return new URLSearchParams(inputs.map(item => `${item.name}=${item.value}`).join('&'));
    }

    handleEmailResponseMessage(response, json, isJson) {
        if (response.ok) {
            if (isJson) {
                this.handleMessage('success', json.message || 'Email has been sent successfully.');
            } else {
                this.handleMessage('success', 'Email has been sent successfully.');
            }
        } else {
            if (isJson) {
                this.handleMessage(
                    'error',
                    'Something went wrong',
                    `${json.code || json.status || 'unknown error'} - ${json.message || ''}`
                );
            } else {
                this.handleMessage(
                    'error',
                    'Something went wrong',
                    `${response.status || ''} - Response was not a Json.`
                );
            }
        }
    }

    async handleDownloadResponseMessage(response) {
        if (response.ok) {
            this.handleMessage('success', 'File has started downloading.');
        } else {
            this.handleMessage(
                'error',
                'File download failed.',
                `${response.status || 'unknown error'} - ${response.statusText || ''}`
            );
        }
    }

    async handleEmailResponse(response) {
        let {json, isJson} = await this.getJson(response);
        this.handleEmailResponseMessage(response, json, isJson);
    }

    async handleDownloadResponse(response) {
        let filename = '',
            contentDisposition = response.headers.get('Content-Disposition'),
            blob = await response.blob();

        await this.handleDownloadResponseMessage(response);
        if (response.ok) {
            if (contentDisposition) {
                const toFind = 'filename=',
                    filenameIndex = contentDisposition.indexOf(toFind);

                filename = contentDisposition.substring(filenameIndex + toFind.length);
            }

            if (typeof window.navigator.msSaveBlob !== 'undefined') {
                window.navigator.msSaveBlob(blob, filename);
            } else {
                const url = window.URL.createObjectURL(blob),
                    a = document.createElement('a');

                a.style.display = 'none';
                a.href = url;
                a.download = filename;
                a.target = '_blank';

                document.body.appendChild(a);
                a.click();
                a.remove();

                window.URL.revokeObjectURL(url);
            }
        }
    }
}
